/*
 * Copyright (C) 2019 SADE Innovations Oy - All Rights Reserved
 *
 * NOTICE: This software is owned by SADE Innovations Oy and licensed under SADE Booster license.
 * All dissemination, usage, modification, copying, reproduction, selling and distribution of the
 * software and its intellectual and technical concepts are strictly forbidden without a valid license.
 * Such license can be obtained by issuing a SADE Booster License agreement from SADE Innovations Oy
 * (https://sadeinnovations.com).
 *
 */

import { Button, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@material-ui/core";
import React, { Component } from "react";
import ErrorDialog from "../../../ui/error-dialog";
import accessControlled from "../../../access-control/access-controlled";
import { Organization, PolicyGroup, isError } from "@sade/data-access";
import Loader from "../../../ui/loader";
import DropdownSelection from "../../../ui/dropdown-selection";
import { isValidEmail } from "../../../../utils/validation";
import { translations } from "../../../../generated/translationHelper";

interface Props {
  organization: Organization;
  availablePolicyGroups: PolicyGroup[];
}

interface State {
  loading: boolean;
  selectedPolicy?: number;
  username?: string;
  errorMsg?: string;
}

export class NewUserForm extends Component<Props, State> {
  public constructor(props: Props) {
    super(props);
    this.state = {
      loading: false,
      selectedPolicy: 0,
    };
  }

  private isValidUsername(): boolean {
    return !!this.state.username && isValidEmail(this.state.username);
  }

  private isValidPolicyGroupSelection(): boolean {
    return this.state.selectedPolicy != null;
  }

  private handleClickNewUser = async (): Promise<void> => {
    if (!this.isValidUsername()) {
      this.setState({ errorMsg: translations.admin.texts.invalidEmailAddress() });
      return;
    }

    if (!this.isValidPolicyGroupSelection()) {
      this.setState({ errorMsg: translations.admin.texts.policyGroupMustBeSelected() });
      return;
    }

    const policyGroup = this.props.availablePolicyGroups[this.state.selectedPolicy!];

    this.setState({ loading: true });

    try {
      const result = await this.props.organization.createUser({
        username: this.state.username!,
        resendInvitation: false,
      });

      await policyGroup.addUser(result);
    } catch (error) {
      let reason = translations.admin.texts.failedToCreateUser();

      if (isError(error) && error.message.search("account already exists") >= 0) {
        reason = translations.admin.texts.userAlreadyExists();
      }
      this.setState({
        errorMsg: reason,
      });
    } finally {
      this.setState({
        username: undefined,
        loading: false,
        selectedPolicy: 0,
      });
    }
  };

  public render(): JSX.Element {
    return (
      <div className="new-user-form">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translations.admin.texts.createNewUser()}</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <TextField
                  fullWidth={true}
                  label={translations.common.inputs.email()}
                  variant="outlined"
                  inputProps={{ "data-testid": "create-user-field" }}
                  type="email"
                  value={this.state.username ?? ""}
                  onChange={(event): void => this.setState({ username: event.currentTarget.value })}
                />
              </TableCell>
              <TableCell align="center">
                <DropdownSelection
                  label={translations.admin.inputs.policyGroup()}
                  onSelect={(index): void => this.setState({ selectedPolicy: index })}
                  selectionList={this.props.availablePolicyGroups.map((pg) => ({
                    key: pg.getId(),
                    label: pg.getName(),
                  }))}
                  currentSelection={this.state.selectedPolicy}
                  data-testid="create-user-permission-dropdown"
                />
              </TableCell>
              <TableCell align="right">
                {this.state.loading ? (
                  <Loader />
                ) : (
                  <Button
                    onClick={this.handleClickNewUser}
                    variant="contained"
                    color="primary"
                    data-testid="create-user-button"
                    disabled={!this.isValidPolicyGroupSelection() || !this.isValidUsername()}
                  >
                    {translations.common.buttons.create()}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <ErrorDialog
          errorMsg={this.state.errorMsg}
          onClose={(): void => this.setState({ errorMsg: undefined })}
          data-testid="create-user-error"
        />
      </div>
    );
  }
}

export default accessControlled(NewUserForm, ["usersCreate", "policyGroupsUsersAdd"]);
